// Shared public Careers page + Apply Modal — reused by all 7 non-school institute sites.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Briefcase, MapPin, IndianRupee, Calendar, Users, ArrowRight, X,
  Upload, Loader2, FileText, Info,
} from "lucide-react";

const SAFE_FILENAME = /^[A-Za-z0-9._ \-]+$/;

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}

/**
 * <PublicCareersPage>
 * Props:
 *  - slug: institute slug (e.g., 'polyaurangabad')
 *  - theme: 'navy' (default) | 'green' | 'cream' — controls hero accent
 *  - eyebrow, heading, tagline: page hero copy
 *  - testidPrefix: e.g., 'poly-careers'
 */
export default function PublicCareersPage({
  slug, theme = "navy", eyebrow = "Careers", heading = "Join our team",
  tagline = "Explore open positions and apply online. We look for teachers, technicians and administrators who share our commitment to student outcomes.",
  testidPrefix = "careers",
}) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [applyFor, setApplyFor] = useState(null);
  const [generalOpen, setGeneralOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/institutes/${slug}/jobs`);
        if (!cancel) setJobs(data || []);
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Failed to load jobs");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const heroBg = theme === "green" ? "bg-[#0F5132]" : theme === "cream" ? "bg-[#8B7355]" : "bg-[#0B1F4D]";
  const accent = theme === "green" ? "#F59E0B" : theme === "cream" ? "#D4A24C" : "#D4A24C";

  return (
    <div data-testid={testidPrefix}>
      <section className={`${heroBg} text-white py-14 relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(45deg, #fff 25%, transparent 25%), linear-gradient(-45deg, #fff 25%, transparent 25%)", backgroundSize: "20px 20px" }} />
        <div className="relative max-w-7xl mx-auto px-5 flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.4em] uppercase font-bold" style={{ color: accent }}>{eyebrow}</div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-serif leading-tight">{heading}</h1>
            <div className="mt-3 h-[2px] w-16" style={{ background: accent }} />
            {tagline && <p className="mt-5 max-w-2xl text-white/80">{tagline}</p>}
          </div>
          <button
            type="button"
            onClick={() => setGeneralOpen(true)}
            data-testid={`${testidPrefix}-general-apply-btn`}
            className="inline-flex items-center gap-2 font-semibold text-xs uppercase tracking-wider px-5 py-3 rounded-sm transition-colors bg-white text-[#0B1F4D] hover:bg-[#f5f1e8]"
          >
            <Upload size={14} /> Send Your Resume
          </button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-12">
        {loading ? (
          <div className="text-sm text-[#6b655b]">Loading…</div>
        ) : err ? (
          <div className="text-sm text-red-700">{err}</div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-14 text-center" data-testid={`${testidPrefix}-empty`}>
            <Briefcase size={40} strokeWidth={1.2} className="mx-auto text-[#bcb6aa]" />
            <div className="mt-3 font-serif text-xl text-[#1c1a17]">No open positions right now</div>
            <div className="mt-2 text-sm text-[#6b655b] max-w-lg mx-auto">
              We&apos;re not actively hiring at the moment, but we&apos;re always keen to hear from passionate educators and staff.
              Send us your resume and we&apos;ll reach out when a matching role opens up.
            </div>
            <button
              type="button"
              onClick={() => setGeneralOpen(true)}
              data-testid={`${testidPrefix}-empty-apply-btn`}
              className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white font-semibold text-xs uppercase tracking-wider px-5 py-3 rounded-sm transition-colors"
            >
              <Upload size={14} /> Send Your Resume
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((j) => (
              <article key={j.id} data-testid={`${testidPrefix}-job-${j.id}`} className="bg-white border border-[#e5e0d8] rounded-sm p-5 md:p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-xl text-[#1c1a17]">{j.title}</h3>
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold bg-[#234333]/10 text-[#234333]">{j.employment_type}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#6b655b]">
                      {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{j.location}</span>}
                      {j.salary_range && <span className="inline-flex items-center gap-1"><IndianRupee size={11} />{j.salary_range}</span>}
                      <span className="inline-flex items-center gap-1"><Users size={11} />{j.openings} opening{j.openings > 1 ? "s" : ""}</span>
                      {j.deadline && <span className="inline-flex items-center gap-1"><Calendar size={11} />Apply by {fmtDate(j.deadline)}</span>}
                    </div>
                    {j.qualification && (
                      <div className="mt-3 text-sm text-[#1c1a17]"><span className="font-semibold">Qualification:</span> {j.qualification}</div>
                    )}
                    {j.description && <div className="mt-2 text-sm text-[#5b5650] whitespace-pre-line">{j.description}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setApplyFor(j)}
                    data-testid={`${testidPrefix}-apply-${j.id}`}
                    className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white font-semibold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors"
                  >
                    Apply Now <ArrowRight size={13} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {applyFor && (
        <ApplyModal
          slug={slug}
          job={applyFor}
          onClose={() => setApplyFor(null)}
          testidPrefix={testidPrefix}
        />
      )}
      {generalOpen && (
        <ApplyModal
          slug={slug}
          job={null}
          onClose={() => setGeneralOpen(false)}
          testidPrefix={testidPrefix}
          general
        />
      )}
    </div>
  );
}

function ApplyModal({ slug, job, onClose, testidPrefix, general = false }) {
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", years_experience: "0", current_profession: "",
  });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    if (f.type !== "application/pdf") { toast.error("Resume must be a PDF file"); e.target.value = ""; return; }
    if (f.size > 500 * 1024) { toast.error("Resume must be 500 KB or smaller"); e.target.value = ""; return; }
    const stem = (f.name || "").replace(/\.pdf$/i, "");
    if (!SAFE_FILENAME.test(stem)) {
      toast.error("Filename must only contain letters, numbers, spaces, dots, hyphens and underscores.");
      e.target.value = "";
      return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your full name");
    const m = form.mobile.replace(/\D/g, "");
    if (m.length !== 10) return toast.error("Mobile must be a 10-digit number");
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast.error("Invalid email");
    if (!resume) return toast.error("Please attach your resume (PDF, 500 KB max)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name.trim());
      fd.append("mobile", m);
      fd.append("email", form.email.trim());
      fd.append("years_experience", String(form.years_experience || 0));
      fd.append("current_profession", form.current_profession.trim());
      fd.append("resume", resume, resume.name);
      const url = general
        ? `/public/institutes/${slug}/apply-general`
        : `/public/institutes/${slug}/jobs/${job.id}/apply`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(general
        ? "Resume received. We'll be in touch when a matching role opens up."
        : "Application submitted. Thank you — we'll be in touch.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full border border-[#e5e0d8] bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#234333]";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" role="dialog" data-testid={general ? `${testidPrefix}-general-modal` : `${testidPrefix}-apply-modal`}>
      <div className="bg-white w-full max-w-2xl mt-6 mb-16 rounded-sm shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-[#e5e0d8] px-6 py-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8b8578] font-bold">{general ? "General Application · Talent Pool" : "Apply for"}</div>
            <h3 className="font-serif text-xl text-[#1c1a17] mt-0.5">{general ? "Send us your resume" : job.title}</h3>
            {general && <div className="mt-1 text-xs text-[#5b5650]">We&apos;ll keep your details on file and contact you when a matching role opens up.</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={general ? `${testidPrefix}-general-close` : `${testidPrefix}-apply-close`} className="text-[#6b655b] hover:text-[#1c1a17]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Full Name *</div>
            <input data-testid={`${testidPrefix}-apply-name`} type="text" maxLength={160} className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required />
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Contact No. *</div>
            <input data-testid={`${testidPrefix}-apply-mobile`} type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} className={inputCls} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} placeholder="10-digit" required />
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Email ID</div>
            <input data-testid={`${testidPrefix}-apply-email`} type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="optional" />
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Years of Experience *</div>
            <select data-testid={`${testidPrefix}-apply-yoe`} className={inputCls} value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)}>
              {Array.from({ length: 51 }).map((_, i) => <option key={i} value={i}>{i} {i === 0 ? "(Fresher)" : (i === 1 ? "year" : "years")}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Resume (PDF, 500 KB max) *</div>
            <input data-testid={`${testidPrefix}-apply-resume`} type="file" accept="application/pdf" onChange={pickFile} className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[#234333] file:text-white file:font-semibold hover:file:bg-[#1a3326]" required />
            {resume && <div className="mt-1 text-[11px] text-[#5b5650] inline-flex items-center gap-1"><FileText size={11} /> {resume.name} · {(resume.size / 1024).toFixed(0)} KB</div>}
            <div className="mt-1 text-[10px] text-[#8b8578] inline-flex items-start gap-1"><Info size={10} className="mt-0.5" /> Filename can only contain letters, numbers, spaces, dots, hyphens and underscores.</div>
          </label>
          <label className="block sm:col-span-2">
            <div className="text-xs font-bold uppercase tracking-wider text-[#234333] mb-1">Current Profession</div>
            <textarea data-testid={`${testidPrefix}-apply-profession`} rows={4} maxLength={2000} className={`${inputCls} resize-y`} value={form.current_profession} onChange={(e) => set("current_profession", e.target.value)} placeholder="Briefly describe your current role, employer, and any relevant experience (2000 characters max)" />
            <div className="mt-1 text-[10px] text-[#8b8578] text-right">{form.current_profession.length}/2000</div>
          </label>
          <div className="sm:col-span-2 flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="submit" disabled={busy} data-testid={`${testidPrefix}-apply-submit`} className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] disabled:opacity-60 text-white font-semibold text-sm uppercase tracking-wider px-5 py-2.5 rounded-sm">
              {busy ? (<><Loader2 size={14} className="animate-spin" /> Submitting…</>) : (<><Upload size={14} /> Submit Application</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
